import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Settings, Car, Calendar, CreditCard, FileText, CheckCircle, MapPin, BarChart3, Users, Shield, Smartphone, X, ZoomIn } from 'lucide-react';

const clientFeatures = [
  { 
    id: 1,
    icon: Car,
    title: 'Catalogue véhicules',
    description: 'Parcourez notre flotte avec filtres par catégorie (SUV, Berline, Citadine, Utilitaire)',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/y660ia05_image.png'
  },
  { 
    id: 2,
    icon: Calendar,
    title: 'Sélection des dates',
    description: 'Calendrier interactif pour choisir vos dates de départ et retour',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/uap6yn9b_image.png'
  },
  { 
    id: 3,
    icon: FileText,
    title: 'Récapitulatif',
    description: 'Vérifiez tous les détails de votre réservation avant de confirmer',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/ndfax7lq_image.png'
  },
  { 
    id: 4,
    icon: CreditCard,
    title: 'Paiement sécurisé',
    description: 'Payez par carte bancaire, TWINT ou en espèces à la prise en charge',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/7dlq12an_image.png'
  },
  { 
    id: 5,
    icon: CheckCircle,
    title: 'Confirmation instantanée',
    description: 'Recevez votre confirmation et téléchargez votre contrat PDF',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/6c1ae5po_image.png'
  },
];

const adminFeatures = [
  { 
    id: 1,
    icon: BarChart3,
    title: 'Dashboard Admin',
    description: 'Vue d\'ensemble avec statistiques en temps réel: véhicules, réservations, clients, revenus',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/qg4q5a70_image.png'
  },
  { 
    id: 2,
    icon: Calendar,
    title: 'Planning Gantt',
    description: 'Visualisez toutes vos réservations sur un calendrier interactif',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/t8aiy6ne_image.png'
  },
  { 
    id: 3,
    icon: Car,
    title: 'Gestion des véhicules',
    description: 'Gérez votre flotte: disponibilité, maintenance, documents',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/9kjyzp55_image.png'
  },
  { 
    id: 4,
    icon: FileText,
    title: 'Fiches véhicules',
    description: 'Informations complètes: photos, caractéristiques, tarifs, documents',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/o4pdemob_image.png'
  },
  { 
    id: 5,
    icon: BarChart3,
    title: 'Analytics avancé',
    description: 'Analysez vos revenus, taux d\'occupation, méthodes de paiement',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/5cyw4tme_image.png'
  },
  { 
    id: 6,
    icon: MapPin,
    title: 'Suivi GPS',
    description: 'Localisez vos véhicules en temps réel sur la carte',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/b96m5rx1_image.png'
  },
  { 
    id: 7,
    icon: Users,
    title: 'Gestion clients',
    description: 'Liste des clients avec recherche et filtres',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/glwn2wih_image.png'
  },
  { 
    id: 8,
    icon: Shield,
    title: 'Fiches clients',
    description: 'Documents d\'identité, permis, historique des locations',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/nk9fs2x2_image.png'
  },
  { 
    id: 9,
    icon: FileText,
    title: 'Modèles de contrats',
    description: 'Personnalisez vos contrats avec logo et tarifs par défaut',
    image: 'https://customer-assets.emergentagent.com/job_fleet-manager-412/artifacts/5ke2ra80_image.png'
  },
];

export const FeaturesShowcase = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('client');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  
  const features = activeTab === 'client' ? clientFeatures : adminFeatures;
  const displayedFeature = selectedFeature || features[0];

  const titles = {
    fr: {
      title: 'Découvrez Logirent en action',
      subtitle: 'Une plateforme complète pour clients et gestionnaires',
      clientTab: 'Espace Client',
      adminTab: 'Espace Admin',
      clientDesc: 'Réservation simple et rapide',
      adminDesc: 'Gestion complète de votre agence',
      clickToZoom: 'Cliquez pour agrandir'
    },
    en: {
      title: 'Discover Logirent in action',
      subtitle: 'A complete platform for clients and managers',
      clientTab: 'Client Area',
      adminTab: 'Admin Area',
      clientDesc: 'Simple and fast booking',
      adminDesc: 'Complete agency management',
      clickToZoom: 'Click to enlarge'
    }
  };

  const t = titles[language] || titles.fr;

  return (
    <section id="showcase" className="py-16 md:py-24 bg-slate-50" data-testid="features-showcase">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-3 mb-10">
          <button
            onClick={() => { setActiveTab('client'); setSelectedFeature(null); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'client' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' 
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-client"
          >
            <User className="w-5 h-5" />
            <div className="text-left">
              <div className="font-semibold">{t.clientTab}</div>
              <div className="text-xs opacity-80">{t.clientDesc}</div>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('admin'); setSelectedFeature(null); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'admin' 
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25' 
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-admin"
          >
            <Settings className="w-5 h-5" />
            <div className="text-left">
              <div className="font-semibold">{t.adminTab}</div>
              <div className="text-xs opacity-80">{t.adminDesc}</div>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="grid md:grid-cols-3 min-h-[500px]">
            {/* Sidebar - Feature List */}
            <div className={`border-r border-slate-100 p-4 overflow-y-auto max-h-[500px] ${
              activeTab === 'client' ? 'bg-blue-50/30' : 'bg-purple-50/30'
            }`}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-2"
                >
                  {features.map((feature, index) => (
                    <motion.button
                      key={feature.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSelectedFeature(feature)}
                      className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all ${
                        displayedFeature?.id === feature.id
                          ? activeTab === 'client'
                            ? 'bg-blue-100 border-blue-200 border'
                            : 'bg-purple-100 border-purple-200 border'
                          : 'hover:bg-white border border-transparent hover:border-slate-200'
                      }`}
                      data-testid={`feature-btn-${feature.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        displayedFeature?.id === feature.id
                          ? activeTab === 'client' ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        <feature.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className={`font-medium text-sm ${
                          displayedFeature?.id === feature.id ? 'text-slate-900' : 'text-slate-700'
                        }`}>
                          {feature.title}
                        </div>
                        <div className="text-xs text-slate-500 line-clamp-2">
                          {feature.description}
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Main Content - Image */}
            <div className="md:col-span-2 p-6 flex flex-col">
              <AnimatePresence mode="wait">
                <motion.div
                  key={displayedFeature?.id + activeTab}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col"
                >
                  {/* Feature Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      activeTab === 'client' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                    }`}>
                      {displayedFeature && <displayedFeature.icon className="w-5 h-5" />}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{displayedFeature?.title}</h3>
                      <p className="text-sm text-slate-500">{displayedFeature?.description}</p>
                    </div>
                  </div>

                  {/* Image */}
                  <div 
                    className="flex-1 bg-slate-100 rounded-xl overflow-hidden border border-slate-200 min-h-[400px] relative group cursor-pointer"
                    onClick={() => setZoomedImage(displayedFeature)}
                  >
                    <img 
                      src={displayedFeature?.image} 
                      alt={displayedFeature?.title}
                      className="w-full h-full object-contain bg-white p-2"
                      data-testid="feature-image"
                    />
                    {/* Zoom overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                        <ZoomIn className="w-4 h-4 text-slate-700" />
                        <span className="text-sm font-medium text-slate-700">{t.clickToZoom}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Feature highlights below */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Smartphone, label: 'Multi-plateforme', desc: 'Web & Mobile' },
            { icon: Shield, label: 'Sécurisé', desc: 'Données chiffrées' },
            { icon: MapPin, label: 'GPS intégré', desc: 'Suivi en temps réel' },
            { icon: FileText, label: 'Contrats digitaux', desc: 'Signature électronique' },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-xl p-4 border border-slate-200 text-center"
            >
              <div className="w-10 h-10 mx-auto bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                <item.icon className="w-5 h-5 text-slate-600" />
              </div>
              <div className="font-semibold text-slate-900 text-sm">{item.label}</div>
              <div className="text-xs text-slate-500">{item.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Zoom Modal */}
      <AnimatePresence>
        {zoomedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setZoomedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-6xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setZoomedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-slate-300 transition-colors flex items-center gap-2"
              >
                <span className="text-sm">Fermer</span>
                <X className="w-6 h-6" />
              </button>
              
              {/* Image title */}
              <div className="absolute -top-12 left-0 text-white">
                <h3 className="font-semibold text-lg">{zoomedImage.title}</h3>
              </div>

              {/* Image */}
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={zoomedImage.image}
                  alt={zoomedImage.title}
                  className="w-full h-auto max-h-[85vh] object-contain"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
